import React, {useEffect} from 'react';
import {Button, Icon, SearchInput} from '@albert-io/atomic';
import {debounce} from 'lodash';
import {IconName} from '@fortawesome/fontawesome-common-types';
import {history} from 'client/history';

import {GlobalAssignmentFilterObj} from '../constants';

import ClassFilter from './ClassFilter.react';
import SortDropdown from './SortDropdown.react';

interface SearchFilterProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  setDebouncedSearchTerm: (value: string) => void;
  setFiltersOpen: (value: boolean) => void;
  filtersOpen: boolean;
  filters: GlobalAssignmentFilterObj;
  sortByOption: string;
  setSortByOption: (value: string) => void;
  isDraftsTab?: boolean;
  setFilters?: (value: GlobalAssignmentFilterObj) => void;
  setFiltersToUse?: (value: GlobalAssignmentFilterObj) => void;
  isMobile?: boolean;
}

const debounceSearchSet = debounce(
  (setDebouncedSearchTerm, value) => setDebouncedSearchTerm(value),
  200
);

const onChange = (e, setSearchTerm, setDebouncedSearchTerm) => {
  setSearchTerm(e.target.value);
  debounceSearchSet(setDebouncedSearchTerm, e.target.value);
};

const onReset = (setSearchTerm, setDebouncedSearchTerm) => {
  setSearchTerm('');
  debounceSearchSet(setDebouncedSearchTerm, '');
};

const boolSumChecker = (filters) => {
  let sum = 0;
  if (filters.status) {
    sum += 1;
  }
  if (Object.keys(filters.classes).length) {
    sum += 1;
  }
  if (filters.shared) {
    sum += 1;
  }
  return sum;
};

const SearchFilter: React.FC<SearchFilterProps> = ({
  searchTerm,
  setSearchTerm,
  setDebouncedSearchTerm,
  setFiltersOpen,
  filtersOpen,
  filters,
  sortByOption,
  setSortByOption,
  isDraftsTab,
  setFilters = () => {},
  setFiltersToUse = () => {},
  isMobile
}) => {
  useEffect(() => {
    return () => {
      debounceSearchSet.cancel();
    };
  }, []);

  const iconNameCircle = boolSumChecker(filters)
    ? `circle-${boolSumChecker(filters)}`
    : 'bars-filter';

  return (
    <div className='global-assignments__search-filter-sort u-gap_space-x1 flex-wrap'>
      <div className='flex items-center'>
        <SearchInput
          rectangle
          onChange={(e) => onChange(e, setSearchTerm, setDebouncedSearchTerm)}
          onReset={() => onReset(setSearchTerm, setDebouncedSearchTerm)}
          placeholder={isMobile ? 'Search' : 'Search by assignment name'}
          aria-label='Search by assignment name'
          className='global-assignments__search'
          value={searchTerm}
        />
        {isMobile && (
          <Button
            onClick={() => history.pushState(null, `/assignment-builder/create-assignment`)}
            className='u-mar-l_1 whitespace-nowrap'
          >
            <Icon icon='plus' className='u-mar-r_1' />
            New Assignment
          </Button>
        )}
      </div>
      <div className='global-assignments__other-filters'>
        {isDraftsTab ? (
          <ClassFilter
            setFilters={setFilters}
            filters={filters}
            isDraftsTab
            setFiltersToUse={setFiltersToUse}
            isMobile={isMobile}
          />
        ) : (
          <Button
            color='secondary'
            variant='outlined'
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`${boolSumChecker(filters) ? 'content-link' : ''}`}
            data-testid='filter-trigger'
          >
            Filters
            <Icon icon={iconNameCircle as IconName} className='u-mar-l_1' />
          </Button>
        )}
        <SortDropdown sortByOption={sortByOption} setSortByOption={setSortByOption} />
      </div>
    </div>
  );
};

export default SearchFilter;
